import { Injectable } from '@angular/core';
import { UnitOfWork } from '@/shared/unit-of-work';
import { AuthService } from '@/shared/auth.service';
import { UserPref } from '@/model/entities/user-pref';
import { DashboardConfig, createDefaultDashboardConfig } from '../widgets/dashboard-widget.interface';

/**
 * Key for dashboard config in UserPref.prefsJson
 * One layout per user
 */
const DASHBOARD_KEY = 'dashboard';

/**
 * Service for loading and saving dashboard configurations.
 * Stores config in the user's UserPref record (shared with UserPrefsService).
 * One dashboard layout per user.
 */
@Injectable({ providedIn: 'root' })
export class DashboardConfigService {
  constructor(
    private uow: UnitOfWork,
    private authService: AuthService
  ) {}

  /**
   * Load dashboard config for the current user.
   * Returns default config if none exists.
   * Always fetches fresh from the server to avoid stale data.
   */
  async loadConfig(): Promise<DashboardConfig> {
    const user = this.authService.getUser();
    if (!user?.loginName) {
      return createDefaultDashboardConfig();
    }

    try {
      const prefs = await this.uow
        .createQuery(UserPref)
        .where({ username: user.loginName })
        .execute();

      if (prefs.length > 0 && prefs[0].prefsJson) {
        try {
          const stored = JSON.parse(prefs[0].prefsJson);

          if (stored[DASHBOARD_KEY]) {
            return stored[DASHBOARD_KEY] as DashboardConfig;
          }
        } catch (e) {
          console.warn('Failed to parse prefsJson', e);
        }
      }
    } catch (e) {
      console.error('Error loading UserPref', e);
    }

    return createDefaultDashboardConfig();
  }

  /**
   * Save dashboard config for the current user.
   */
  async saveConfig(config: DashboardConfig): Promise<void> {
    const user = this.authService.getUser();
    if (!user?.loginName) {
      console.warn('Cannot save dashboard config: no user logged in');
      return;
    }

    // Create a clean copy to save (to capture current x, y, cols, rows values)
    const configToSave: DashboardConfig = {
      version: config.version,
      widgets: config.widgets.map(w => ({
        id: w.id,
        widgetType: w.widgetType,
        x: w.x,
        y: w.y,
        cols: w.cols,
        rows: w.rows,
      })),
      kpiRange: config.kpiRange,
    };

    // Always fetch fresh to get the latest state (UserPrefsService may have modified it)
    const prefs = await this.uow
      .createQuery(UserPref)
      .where({ username: user.loginName })
      .execute();

    let existingData: Record<string, unknown> = {};

    let pref: UserPref;

    if (prefs.length > 0) {
      pref = prefs[0];
      // Update existing pref - preserve other data in prefsJson
      if (pref.prefsJson) {
        try {
          existingData = JSON.parse(pref.prefsJson);
        } catch {
          existingData = {};
        }
      }
      existingData[DASHBOARD_KEY] = configToSave;
      pref.prefsJson = JSON.stringify(existingData);
    } else {
      // Create new pref
      const newData = { [DASHBOARD_KEY]: configToSave };
      pref = this.uow.createEntity(UserPref, {
        username: user.loginName,
        prefsJson: JSON.stringify(newData),
      });
    }

    // This row and nothing else. An argument-less commit saves every pending change in the manager,
    // so saving a dashboard layout would also save whatever form the operator has open elsewhere.
    await this.uow.commit([pref]);
  }
}
